/**
 * TrackingControl Component
 * 
 * Displays GPS tracking status and controls for the native mobile app.
 * Shows a toggle to enable/disable automatic mileage tracking.
 * Includes setup wizard prompt for first-time users.
 */

import React, { useState, useEffect } from 'react';
import { useGeolocation } from '../../hooks/useGeolocation';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Navigation, 
  MapPin, 
  Loader2, 
  Power, 
  AlertTriangle,
  Smartphone,
  Route,
  Settings,
  HelpCircle,
  CheckCircle2
} from 'lucide-react';
import { TrackingSetupWizard } from './TrackingSetupWizard';
import { TrackingHealthCheck } from './TrackingHealthCheck';

export function TrackingControl() {
  const {
    isInitialized,
    isNativePlatform,
    isTracking,
    currentTrip,
    lastLocation,
    error,
    toggleTracking,
    startTracking,
    openSettings,
  } = useGeolocation();

  const [showWizard, setShowWizard] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [showHealthCheck, setShowHealthCheck] = useState(false);

  // Check if setup was completed
  useEffect(() => {
    const setupDone = localStorage.getItem('servio_tracking_setup_complete') === 'true';
    setSetupComplete(setupDone);
    
    // Auto-show wizard for first-time users on native
    if (isNativePlatform && isInitialized && !setupDone) {
      // Small delay to let the UI settle
      const timer = setTimeout(() => setShowWizard(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isNativePlatform, isInitialized]);

  // Don't render anything if not on native platform
  if (!isNativePlatform) {
    return null;
  }

  // Loading state
  if (!isInitialized) {
    return (
      <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            <span className="text-sm text-slate-500">Initializing GPS tracking...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // First-time setup prompt (if setup not complete and not tracking)
  if (!setupComplete && !isTracking) {
    return (
      <>
        <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <Navigation className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                  Set Up Automatic Mileage Tracking
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  Track your business miles automatically. We'll guide you through the device settings 
                  needed for reliable background tracking.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => setShowWizard(true)}>
                    <Settings className="h-4 w-4 mr-1" />
                    Start Setup
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      localStorage.setItem('servio_tracking_setup_complete', 'true');
                      setSetupComplete(true);
                    }}
                  >
                    Skip for now
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <TrackingSetupWizard 
          open={showWizard} 
          onClose={() => setShowWizard(false)}
          onComplete={() => {
            setSetupComplete(true);
            // Auto-start tracking after setup
            startTracking();
          }}
        />
      </>
    );
  }

  return (
    <>
      <Card className="border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Status */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`p-2 rounded-full ${isTracking ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                {isTracking ? (
                  <Navigation className="h-5 w-5 text-green-600 dark:text-green-400 animate-pulse" />
                ) : (
                  <Smartphone className="h-5 w-5 text-slate-500" />
                )}
              </div>
              
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-slate-900 dark:text-slate-100">
                    Auto Tracking
                  </span>
                  <Badge 
                    variant={isTracking ? 'default' : 'secondary'}
                    className={isTracking ? 'bg-green-600' : ''}
                  >
                    {isTracking ? 'Active' : 'Off'}
                  </Badge>
                  {setupComplete && (
                    <button
                      onClick={() => setShowWizard(true)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <Settings className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                
                {/* Current trip info */}
                {currentTrip && (
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
                    <Route className="h-3 w-3" />
                    <span>
                      Trip in progress: {currentTrip.distanceMiles?.toFixed(2) || '0.00'} mi
                    </span>
                    <span className="text-slate-400">•</span>
                    <span>{currentTrip.pointCount} points</span>
                  </div>
                )}
                
                {/* Last location timestamp */}
                {!currentTrip && lastLocation && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-slate-500 dark:text-slate-400">
                    <MapPin className="h-3 w-3" />
                    <span>
                      Last updated: {new Date(lastLocation.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                )}
                
                {/* Error state */}
                {error && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{error.message || 'Location error'}</span>
                    {error.type === 'permission' && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-blue-500"
                        onClick={() => setShowWizard(true)}
                      >
                        Fix Settings
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Right side - Toggle button */}
            <Button
              size="sm"
              variant={isTracking ? 'destructive' : 'default'}
              onClick={toggleTracking}
              className="shrink-0"
              data-testid="tracking-toggle-btn"
            >
              <Power className="h-4 w-4 mr-1" />
              {isTracking ? 'Stop' : 'Start'}
            </Button>
          </div>
          
          {/* Help text */}
          {!isTracking && !error && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 pl-11">
              Enable auto tracking to automatically log your mileage while driving. 
              Trips will be saved when you stop moving for 5+ minutes.
            </p>
          )}
        </CardContent>
      </Card>

      <TrackingSetupWizard 
        open={showWizard} 
        onClose={() => setShowWizard(false)}
        onComplete={() => setSetupComplete(true)}
      />
    </>
  );
}

export default TrackingControl;
